import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type { DeploymentSummary } from '../api/types.ts';
import { Badge, Button, Card, EmptyState, Skeleton } from '../components/ui.tsx';
import { DEPLOYMENT_STATUS, presentation } from '../status.ts';
import './Deployments.css';

export function Deployments({
  onOpen,
  onStart,
}: {
  onOpen: (id: string) => void;
  onStart: () => void;
}): ReactNode {
  const { t } = useI18n();
  const [deployments, setDeployments] = useState<readonly DeploymentSummary[] | undefined>();
  // Separate from an empty list on purpose. `catch(() => [])` renders "you
  // have not installed anything yet" to a customer whose request failed —
  // a failure shown as a fact, and the most alarming possible one.
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setFailed(false);
    try {
      setDeployments(await api.deployments());
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, [load]);

  return (
    <>
      <header className="page-header">
        <h1>{t('deployments.title')}</h1>
        <Button onClick={onStart}>{t('nav.newDeployment')}</Button>
      </header>

      {failed ? (
        <Card>
          <EmptyState
            title={t('error.network')}
            action={
              <Button variant="secondary" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : deployments === undefined ? (
        <Card>
          <Skeleton lines={4} />
        </Card>
      ) : deployments.length === 0 ? (
        <Card>
          <EmptyState
            title={t('deployments.empty')}
            action={<Button onClick={onStart}>{t('nav.newDeployment')}</Button>}
          />
        </Card>
      ) : (
        <DeploymentList deployments={deployments} onOpen={onOpen} />
      )}
    </>
  );
}

export function DeploymentList({
  deployments,
  onOpen,
}: {
  deployments: readonly DeploymentSummary[];
  onOpen: (id: string) => void;
}): ReactNode {
  const { t, formatDateTime } = useI18n();

  return (
    <ul className="deployment-list">
      {deployments.map((deployment) => {
        const status = presentation(DEPLOYMENT_STATUS, deployment.status, {
          label: 'deployments.statusDraft',
          tone: 'neutral',
        });
        return (
          <li key={deployment.id}>
            <button type="button" className="deployment-row" onClick={() => onOpen(deployment.id)}>
              <span className="deployment-row__main">
                <strong>{deployment.panelAdapterId.replace(/^panel-/, '')}</strong>
                <span className="subtle">{formatDateTime(deployment.createdAt)}</span>
              </span>
              <Badge tone={status.tone}>{t(status.label)}</Badge>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
